import * as React from 'react';
import {
  FileText,
  MessageCircle,
  Mic,
  Paperclip,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Send,
  Square,
  Video,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSupportCall } from '@most/webrtc-call';
import type { ChatMessage } from '@most/api-types';
import { Button, cn } from '@/components/ui';
import {
  chatCallApi,
  chatEvents,
  useChatConversation,
  useChatMessages,
  useMarkChatRead,
  useSendChatMessage,
  useUploadChatAttachment,
  type ChatUploadKind,
} from '@/shared/chat';

/**
 * The floating support-chat widget (bottom-right, every producer screen).
 *
 * The other party is always "support": the API never says who answers and neither does the
 * UI. Message direction is the `from_admin` bit — that is the entire sender model, and it
 * is what keeps this surface inside the anonymity matrix.
 */
export function SupportChat() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const conversation = useChatConversation();
  const messagesQuery = useChatMessages(open);
  const send = useSendChatMessage();
  const uploadMut = useUploadChatAttachment();
  const markRead = useMarkChatRead();

  const call = useSupportCall({
    api: chatCallApi,
    side: 'user',
    onTerminal: () => void messagesQuery.refetch(),
  });

  React.useEffect(
    () =>
      chatEvents.subscribe((e) => {
        if (!e.id) return;
        if (e.type === 'call.state') call.onCallStateFrame(e.id);
        if (e.type === 'call.signal') call.onCallSignalFrame(e.id);
      }),
    [call.onCallStateFrame, call.onCallSignalFrame], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const unread = conversation.data?.unread_count ?? 0;

  React.useEffect(() => {
    if (open && unread > 0 && !markRead.isPending) {
      markRead.mutate();
    }
  }, [open, unread]); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = React.useMemo(() => {
    const pages = messagesQuery.data?.pages ?? [];
    return pages.flatMap((p) => p.items).reverse();
  }, [messagesQuery.data]);

  const listRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages.length]);

  const [draft, setDraft] = React.useState('');
  const submitText = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body, { onSuccess: () => setDraft('') });
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const onPickFile = (file: File) => {
    const kind: ChatUploadKind = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
        ? 'video'
        : file.type.startsWith('audio/')
          ? 'audio'
          : 'file';
    uploadMut.mutate({ kind, file });
  };

  const [recording, setRecording] = React.useState(false);
  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const canRecord = typeof window !== 'undefined' && 'MediaRecorder' in window;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: mime });
        if (blob.size > 0) uploadMut.mutate({ kind: 'audio', file: blob });
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      // Mic denied — nothing to do.
    }
  };
  const stopRecording = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  const timeFmt = React.useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    [i18n.language],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t('chat.close') : t('chat.open')}
        className={cn(
          'fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full',
          'bg-primary text-primary-foreground shadow-xl transition hover:scale-105',
        )}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('chat.title')}
          className="fixed bottom-24 right-4 z-50 flex h-[560px] max-h-[calc(100dvh-7rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        >
          <header className="flex items-center gap-2 border-b border-border bg-rail px-4 py-3 text-white">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{t('chat.title')}</div>
              <div className="truncate text-xs text-white/60">{t('chat.subtitle')}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 hover:text-white"
              aria-label={t('chat.call.audio')}
              disabled={call.phase !== 'idle'}
              onClick={() => void call.startCall('audio')}
            >
              <Phone className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 hover:text-white"
              aria-label={t('chat.call.video')}
              disabled={call.phase !== 'idle'}
              onClick={() => void call.startCall('video')}
            >
              <Video className="h-4 w-4" />
            </Button>
          </header>

          <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto bg-muted/40 p-3">
            {messagesQuery.hasNextPage && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={messagesQuery.isFetchingNextPage}
                  onClick={() => void messagesQuery.fetchNextPage()}
                >
                  {t('chat.loadOlder')}
                </Button>
              </div>
            )}
            {messages.length === 0 && !messagesQuery.isLoading && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('chat.empty')}</p>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} timeFmt={timeFmt} />
            ))}
          </div>

          <footer className="border-t border-border bg-background p-2">
            <div className="flex items-end gap-1">
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,audio/mpeg,audio/ogg,audio/webm,audio/mp4,application/pdf,application/zip,.docx,.xlsx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onPickFile(f);
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('chat.attach')}
                disabled={uploadMut.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              {canRecord && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={recording ? t('chat.stopRecording') : t('chat.record')}
                  className={cn(recording && 'text-red-600')}
                  onClick={() => (recording ? stopRecording() : void startRecording())}
                >
                  {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              )}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitText();
                  }
                }}
                rows={1}
                placeholder={t('chat.placeholder')}
                className="max-h-28 min-h-10 flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
              />
              <Button
                size="icon"
                aria-label={t('chat.send')}
                disabled={!draft.trim() || send.isPending}
                onClick={submitText}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {(send.isError || uploadMut.isError) && (
              <p className="px-2 pt-1 text-xs text-destructive">{t('chat.sendError')}</p>
            )}
          </footer>
        </div>
      )}

      <CallOverlay call={call} />
    </>
  );
}

function MessageBubble({ message: m, timeFmt }: { message: ChatMessage; timeFmt: Intl.DateTimeFormat }) {
  const { t } = useTranslation();
  const mine = !m.from_admin;
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
          mine
            ? 'rounded-br-sm bg-rail text-white'
            : 'rounded-bl-sm border border-border bg-background text-foreground',
        )}
      >
        {m.kind === 'call' && m.call ? (
          <span className="flex items-center gap-1.5">
            {m.call.kind === 'video' ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
            {t(`chat.call.${m.call.outcome}`)}
            {m.call.seconds != null &&
              ` · ${Math.floor(m.call.seconds / 60)}:${String(m.call.seconds % 60).padStart(2, '0')}`}
          </span>
        ) : (
          <>
            {m.kind === 'image' && m.attachment && (
              <a href={m.attachment.url} target="_blank" rel="noreferrer">
                <img
                  src={m.attachment.thumb_url ?? m.attachment.url}
                  alt={m.attachment.name}
                  className="mb-1 max-h-48 rounded-lg"
                  loading="lazy"
                />
              </a>
            )}
            {m.kind === 'video' && m.attachment && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={m.attachment.url} controls preload="metadata" className="mb-1 max-h-48 w-full rounded-lg" />
            )}
            {m.kind === 'audio' && m.attachment && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio src={m.attachment.url} controls preload="metadata" className="mb-1 w-56 max-w-full" />
            )}
            {m.kind === 'file' && m.attachment && (
              <a
                href={m.attachment.url}
                download={m.attachment.name}
                className={cn(
                  'mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5',
                  mine ? 'border-white/20' : 'border-border',
                )}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium">{m.attachment.name}</span>
                  <span className={cn('block text-[10px]', mine ? 'text-white/60' : 'text-muted-foreground')}>
                    {formatSize(m.attachment.size)}
                  </span>
                </span>
              </a>
            )}
            {m.body && <span className="whitespace-pre-wrap break-words">{m.body}</span>}
          </>
        )}
        <div className="mt-1 text-right text-[10px] opacity-60">
          {timeFmt.format(new Date(m.created_at))}
          {mine && m.read_at ? ' ✓✓' : mine ? ' ✓' : ''}
        </div>
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}

function CallOverlay({ call }: { call: ReturnType<typeof useSupportCall> }) {
  const { t } = useTranslation();
  const remoteRef = React.useRef<HTMLVideoElement>(null);
  const localRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = call.remoteStream;
  }, [call.remoteStream, call.phase]);
  React.useEffect(() => {
    if (localRef.current) localRef.current.srcObject = call.localStream;
  }, [call.localStream, call.phase]);

  if (call.phase === 'idle') return null;

  const isVideo = call.call?.kind === 'video';
  const statusKey =
    call.phase === 'outgoing'
      ? 'chat.call.outgoing'
      : call.phase === 'incoming'
        ? 'chat.call.incoming'
        : call.phase === 'connecting'
          ? 'chat.call.connecting'
          : call.phase === 'active'
            ? 'chat.call.active'
            : 'chat.call.endedStatus';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl bg-rail p-6 text-white shadow-2xl">
        <p className="flex items-center gap-2 text-sm text-white/70">
          {call.phase === 'incoming' ? (
            <PhoneIncoming className="h-4 w-4 animate-pulse text-green-400" />
          ) : isVideo ? (
            <Video className="h-4 w-4" />
          ) : (
            <Phone className="h-4 w-4" />
          )}
          {t(statusKey)}
        </p>

        <div className="relative w-full">
          <video
            ref={remoteRef}
            autoPlay
            playsInline
            className={cn('w-full rounded-xl bg-black', isVideo ? 'aspect-video' : 'h-24 opacity-40')}
          />
          {isVideo && (
            <video
              ref={localRef}
              autoPlay
              playsInline
              muted
              className="absolute bottom-2 right-2 w-24 rounded-lg border border-white/30 bg-black"
            />
          )}
        </div>

        {call.error && (
          <p className="text-xs text-red-400">
            {t(
              call.error === 'media_denied'
                ? 'chat.call.mediaDenied'
                : call.error === 'connection_lost'
                  ? 'chat.call.connectionLost'
                  : 'chat.call.failed',
            )}
          </p>
        )}

        <div className="flex items-center gap-3">
          {call.phase === 'incoming' ? (
            <>
              <Button className="bg-green-600 text-white hover:bg-green-500" onClick={() => void call.accept()}>
                <Phone className="mr-2 h-4 w-4" /> {t('chat.call.accept')}
              </Button>
              <Button variant="destructive" onClick={() => void call.decline()}>
                <PhoneOff className="mr-2 h-4 w-4" /> {t('chat.call.decline')}
              </Button>
            </>
          ) : (
            <Button variant="destructive" onClick={() => void call.hangUp()}>
              <PhoneOff className="mr-2 h-4 w-4" /> {t('chat.call.hangUp')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
